/**
 * Карточка переписки — то, что оператор читает вместо сорока сообщений.
 *
 * Живёт над диалогом и одна на все каналы: почта, телеграм и виджет
 * показывают её одинаково.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import {
  useConversationSummary,
  useRefreshSummary,
  refreshErrorText,
  type SummaryContent,
  type SummaryStatus,
} from '../../api/summary';
import type { ConversationSource } from '../../lib/inbox-id';
import { formatRelativeTime } from '../../lib/date-utils';

const COLLAPSE_KEY = 'anima:summary-collapsed';

/** Свёрнутость запоминается: оператор открывает десятки диалогов за смену. */
function useCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // приватный режим — просто не запомним
    }
  }, [collapsed]);

  return [collapsed, useCallback(() => setCollapsed((v) => !v), [])];
}

export function ConversationSummary({
  source,
  totalMessages,
}: {
  source: ConversationSource;
  /** Сколько сообщений в диалоге — для порога устаревания */
  totalMessages?: number;
}): JSX.Element | null {
  const { data, isLoading } = useConversationSummary(source);
  const { mutate: refresh, isPending, error: refreshError, reset } = useRefreshSummary();
  const [collapsed, toggleCollapsed] = useCollapsed();

  // Отказ относится к конкретному диалогу: на соседнем он ничего не значит
  useEffect(() => {
    reset();
  }, [source.sourceType, source.sourceId, reset]);

  const status: SummaryStatus = data?.status ?? 'idle';
  const building = isPending || status === 'queued' || status === 'running';

  // Автообновление при открытии — только на открытый диалог, не на список:
  // инбокс на тридцать переписок не должен ставить тридцать задач.
  // Порог берём с бэкенда, чтобы правило не разъехалось
  const uncovered = (totalMessages ?? 0) - (data?.coveredMessageCount ?? 0);
  const stale = data?.staleAfter !== undefined && uncovered >= data.staleAfter;

  useEffect(() => {
    if (stale && !building && status !== 'failed') {
      refresh(source);
    }
    // Один заход на диалог: следующий — когда снова накопится порог
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stale, source.sourceType, source.sourceId]);

  if (isLoading) return null;

  const summary = data?.summary ?? null;

  return (
    <section className="border-b border-border-subtle bg-surface-elevated">
      <header className="flex items-center gap-2 px-4 py-2">
        <button
          type="button"
          onClick={toggleCollapsed}
          className="flex items-center gap-1.5 text-sm font-medium text-text-primary hover:text-accent-brand transition-colors"
          aria-expanded={!collapsed}
        >
          <span className={`text-text-muted transition-transform ${collapsed ? '' : 'rotate-90'}`}>
            ›
          </span>
          Суть переписки
        </button>

        {building && (
          <span className="flex items-center gap-1.5 text-xs text-text-secondary">
            <Spinner size="sm" />
            собираем
          </span>
        )}

        {!building && status === 'failed' && (
          <span className="text-xs text-accent-danger">не удалось обновить</span>
        )}

        {!building && data?.generatedAt && (
          <span className="text-xs text-text-muted">
            {formatRelativeTime(data.generatedAt)}
          </span>
        )}

        <div className="ml-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refresh(source)}
            disabled={building}
          >
            {summary ? 'Обновить' : 'Собрать'}
          </Button>
        </div>
      </header>

      {/* Видна и в свёрнутом виде: отказ относится к карточке целиком,
          а не к её содержимому. Показывается и после автообновления
          по порогу устаревания, которое оператор не нажимал */}
      {refreshError !== null && !building && (
        <p className="px-4 pb-2 text-sm text-accent-danger" role="alert">
          {refreshErrorText(refreshError)}
        </p>
      )}

      {!collapsed && (
        <div className="px-4 pb-3">
          {summary ? (
            <SummaryBody content={summary} dimmed={building} />
          ) : (
            <EmptySummary status={status} building={building} />
          )}
        </div>
      )}
    </section>
  );
}

function EmptySummary({
  status,
  building,
}: {
  status: SummaryStatus;
  building: boolean;
}): JSX.Element {
  if (building) {
    return (
      <p className="text-sm text-text-secondary">
        Читаем переписку — карточка появится через несколько секунд.
      </p>
    );
  }
  if (status === 'failed') {
    return (
      <p className="text-sm text-text-secondary">
        Собрать карточку не получилось. Можно попробовать ещё раз — переписка
        при этом не пострадала.
      </p>
    );
  }
  return (
    <p className="text-sm text-text-secondary">
      Карточки пока нет. Нажмите «Собрать» — и увидите суть переписки, не читая
      её целиком.
    </p>
  );
}

function SummaryBody({
  content,
  dimmed,
}: {
  content: SummaryContent;
  dimmed: boolean;
}): JSX.Element {
  return (
    <div className={`space-y-3 transition-opacity ${dimmed ? 'opacity-50' : ''}`}>
      <div>
        <p className="text-base font-medium text-text-primary">{content.topic}</p>
        <p className="text-sm text-text-secondary mt-0.5">{content.clientWants}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <SummaryList
          title="Договорились"
          items={content.agreed}
          empty="пока ничего"
          accent="text-accent-success"
        />
        <SummaryList
          title="Без ответа"
          items={content.openQuestions}
          empty="всё закрыто"
          accent="text-accent-warning"
        />
      </div>

      <div className="flex items-start gap-2 rounded-comp bg-accent-brand-light border border-accent-brand-border px-3 py-2">
        <span className="text-accent-brand text-sm font-medium shrink-0">Дальше</span>
        <span className="text-sm text-text-primary">{content.nextStep}</span>
      </div>
    </div>
  );
}

function SummaryList({
  title,
  items,
  empty,
  accent,
}: {
  title: string;
  items: string[];
  empty: string;
  accent: string;
}): JSX.Element {
  return (
    <div>
      <p className={`text-xs font-medium uppercase tracking-wide ${accent}`}>{title}</p>
      {items.length === 0 ? (
        <p className="text-sm text-text-muted mt-1">{empty}</p>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {items.map((item) => (
            <li key={item} className="text-sm text-text-primary flex gap-1.5">
              <span className="text-text-muted shrink-0">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
